import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Plus,
  Trash2,
  Pencil,
  X,
  Check,
  Columns3,
  Rows3,
  AlertCircle,
  Download,
  Loader2,
  FileSpreadsheet,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";
import Papa from "papaparse";

import { Header } from "@/components/layout/Header";
import { AdminGuard } from "@/lib/auth/AdminGuard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  getReferenceTable,
  updateReferenceTable,
} from "@/lib/api/references";
import { getErrorMessage } from "@/lib/api/client";
import type { ReferenceTable, ColumnDefinition } from "@/types";

type ColumnType = "string" | "number" | "boolean";

// Reserved field name for storing Excel cell references
const CELL_REFS_FIELD = "_cell_refs";

// Type for cell references - maps column name to Excel cell reference
type CellRefs = Record<string, string>;

interface EditingRow {
  index: number;
  data: Record<string, unknown>;
  cellRefs: CellRefs;
}

export const Route = createFileRoute('/_dashboard/admin/references/$id')({
  component: ReferenceTableDetailPage,
})

function ReferenceTableDetailPage() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const tableId = parseInt(id);

  // State for columns management
  const [columnDialogOpen, setColumnDialogOpen] = useState(false);
  const [editingColumn, setEditingColumn] = useState<ColumnDefinition | null>(null);
  const [columnForm, setColumnForm] = useState<ColumnDefinition>({
    name: "",
    type: "string",
    required: false,
  });
  const [deleteColumnDialog, setDeleteColumnDialog] = useState<string | null>(null);

  // State for rows management
  const [editingRow, setEditingRow] = useState<EditingRow | null>(null);
  const [addRowDialogOpen, setAddRowDialogOpen] = useState(false);
  const [newRowData, setNewRowData] = useState<Record<string, unknown>>({});
  const [newRowCellRefs, setNewRowCellRefs] = useState<CellRefs>({});
  const [deleteRowIndex, setDeleteRowIndex] = useState<number | null>(null);

  // State for inline header editing
  const [editingHeader, setEditingHeader] = useState(false);
  const [headerForm, setHeaderForm] = useState({ name: "", description: "" });

  // State for auto-fill Excel refs
  const [autoFillDialogOpen, setAutoFillDialogOpen] = useState(false);
  const [autoFillStartRef, setAutoFillStartRef] = useState("");

  const { data: table, isLoading, error } = useQuery({
    queryKey: ["reference", tableId],
    queryFn: () => getReferenceTable(tableId),
  });

  const updateMutation = useMutation({
    mutationFn: (data: { name?: string; description?: string | null; columns?: ColumnDefinition[]; data?: Record<string, unknown>[] }) =>
      updateReferenceTable(tableId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reference", tableId] });
      queryClient.invalidateQueries({ queryKey: ["references"] });
      toast.success("Enregistré");
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  // Get columns and data from server
  const columns = table?.columns ?? [];
  const data = table?.data ?? [];

  // Column management handlers
  const handleAddColumn = () => {
    setEditingColumn(null);
    setColumnForm({ name: "", type: "string", required: false });
    setColumnDialogOpen(true);
  };

  const handleEditColumn = (column: ColumnDefinition) => {
    setEditingColumn(column);
    setColumnForm({ ...column });
    setColumnDialogOpen(true);
  };

  const handleSaveColumn = () => {
    if (!columnForm.name.trim()) {
      toast.error("Le nom de la colonne est requis");
      return;
    }

    const newColumns = [...columns];
    let newData = [...data];
    
    if (editingColumn) {
      // Edit existing column
      const index = newColumns.findIndex((c) => c.name === editingColumn.name);
      if (index !== -1) {
        const oldName = editingColumn.name;
        const newName = columnForm.name;
        newColumns[index] = { ...columnForm };
        
        // Update data if column name changed
        if (oldName !== newName) {
          newData = data.map((row) => {
            const newRow = { ...row };
            if (oldName in newRow) {
              newRow[newName] = newRow[oldName];
              delete newRow[oldName];
            }
            return newRow;
          });
        }
      }
    } else {
      // Check for duplicate name
      if (newColumns.some((c) => c.name === columnForm.name)) {
        toast.error("Une colonne avec ce nom existe déjà");
        return;
      }
      newColumns.push({ ...columnForm });
      
      // Add default value to all existing rows
      newData = data.map((row) => ({
        ...row,
        [columnForm.name]: getDefaultValue(columnForm.type),
      }));
    }

    // Save immediately
    updateMutation.mutate({ columns: newColumns, data: newData });
    setColumnDialogOpen(false);
  };

  const handleDeleteColumn = (columnName: string) => {
    const newColumns = columns.filter((c) => c.name !== columnName);
    const newData = data.map((row) => {
      const newRow = { ...row };
      delete newRow[columnName];
      return newRow;
    });
    
    // Save immediately
    updateMutation.mutate({ columns: newColumns, data: newData });
    setDeleteColumnDialog(null);
  };

  // Row management handlers
  const handleAddRow = () => {
    const defaultRow: Record<string, unknown> = {};
    columns.forEach((col) => {
      defaultRow[col.name] = getDefaultValue(col.type);
    });
    setNewRowData(defaultRow);
    setNewRowCellRefs({});
    setAddRowDialogOpen(true);
  };

  const handleSaveNewRow = () => {
    // Include cell refs if any were provided
    const rowToSave = { ...newRowData };
    const nonEmptyRefs = Object.fromEntries(
      Object.entries(newRowCellRefs).filter(([, v]) => v.trim() !== "")
    );
    if (Object.keys(nonEmptyRefs).length > 0) {
      rowToSave[CELL_REFS_FIELD] = nonEmptyRefs;
    }
    const newData = [...data, rowToSave];
    // Save immediately
    updateMutation.mutate({ data: newData });
    setAddRowDialogOpen(false);
    setNewRowData({});
    setNewRowCellRefs({});
  };

  const handleEditRow = (index: number) => {
    const rowData = data[index];
    const existingCellRefs = (rowData[CELL_REFS_FIELD] as CellRefs) || {};
    setEditingRow({
      index,
      data: { ...rowData },
      cellRefs: { ...existingCellRefs },
    });
  };

  const handleSaveRow = () => {
    if (editingRow === null) return;
    
    const newData = [...data];
    const rowToSave = { ...editingRow.data };
    
    // Update cell refs - filter out empty ones
    const nonEmptyRefs = Object.fromEntries(
      Object.entries(editingRow.cellRefs).filter(([, v]) => v.trim() !== "")
    );
    if (Object.keys(nonEmptyRefs).length > 0) {
      rowToSave[CELL_REFS_FIELD] = nonEmptyRefs;
    } else {
      delete rowToSave[CELL_REFS_FIELD];
    }
    
    newData[editingRow.index] = rowToSave;
    // Save immediately
    updateMutation.mutate({ data: newData });
    setEditingRow(null);
  };

  const handleCancelEditRow = () => {
    setEditingRow(null);
  };

  const handleDeleteRow = (index: number) => {
    const newData = data.filter((_, i) => i !== index);
    // Save immediately
    updateMutation.mutate({ data: newData });
    setDeleteRowIndex(null);
  };

  // Export to CSV
  const handleExport = () => {
    if (data.length === 0) {
      toast.error("Aucune donnée à exporter");
      return;
    }
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${table?.slug || "export"}.csv`;
    link.click();
  };

  // Auto-fill Excel references
  const handleAutoFillExcelRefs = () => {
    if (!autoFillStartRef.trim()) {
      toast.error("Veuillez entrer une référence de départ");
      return;
    }

    const parsed = parseExcelRef(autoFillStartRef.trim());
    if (!parsed) {
      toast.error("Format de référence invalide", {
        description: "Utilisez le format: 'NomFeuille'!A1 ou NomFeuille!A1"
      });
      return;
    }

    const columnNames = columns.map((c) => c.name);
    const refsGrid = generateExcelRefsGrid(autoFillStartRef.trim(), data.length, columnNames);

    const newData = data.map((row, rowIdx) => {
      const newRow = { ...row };
      const cellRefs = refsGrid.get(rowIdx);
      if (cellRefs && Object.keys(cellRefs).length > 0) {
        newRow[CELL_REFS_FIELD] = cellRefs;
      }
      return newRow;
    });

    updateMutation.mutate({ data: newData });
    setAutoFillDialogOpen(false);
    toast.success("Références Excel remplies", {
      description: `${data.length} lignes × ${columns.length} colonnes`
    });
  };

  // Preview the auto-fill result
  const autoFillPreview = autoFillStartRef.trim() 
    ? (() => {
        const parsed = parseExcelRef(autoFillStartRef.trim());
        if (!parsed) return null;
        
        const columnNames = columns.map((c) => c.name);
        const refsGrid = generateExcelRefsGrid(autoFillStartRef.trim(), Math.min(data.length, 3), columnNames);
        
        return {
          isValid: true,
          parsed,
          preview: Array.from(refsGrid.entries()).map(([rowIdx, refs]) => ({
            rowIdx,
            refs
          }))
        };
      })()
    : null;

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Chargement..." />
        <div className="flex-1 p-6 space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-[400px] w-full" />
        </div>
      </div>
    );
  }

  if (error || !table) {
    return (
      <div className="flex flex-col h-full">
        <Header title="Erreur" />
        <div className="flex-1 p-6 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <AlertCircle className="h-12 w-12 mx-auto text-destructive mb-4" />
              <p className="text-lg font-medium mb-2">Table non trouvée</p>
              <p className="text-muted-foreground mb-4">
                Cette table de référence n&apos;existe pas ou a été supprimée.
              </p>
              <Button asChild>
                <Link to="/admin/references">Retour aux tables</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <AdminGuard>
    <div className="flex flex-col h-full">
      <Header title={table.name} />

      <div className="flex-1 p-6 space-y-6 overflow-auto">
        {/* Breadcrumb and actions */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" asChild>
              <Link to="/admin/references">
                <ArrowLeft className="h-4 w-4" />
              </Link>
            </Button>
            {editingHeader ? (
              <div className="flex-1 space-y-2 max-w-lg">
                <div className="flex items-center gap-2">
                  <Input
                    value={headerForm.name}
                    onChange={(e) => setHeaderForm({ ...headerForm, name: e.target.value })}
                    className="text-lg font-bold h-10"
                    placeholder="Nom de la table"
                    autoFocus
                  />
                  <code className="px-2 py-1 bg-muted rounded text-xs shrink-0">
                    {table.slug}
                  </code>
                </div>
                <Textarea
                  value={headerForm.description}
                  onChange={(e) => setHeaderForm({ ...headerForm, description: e.target.value })}
                  placeholder="Description (optionnel)"
                  rows={2}
                  className="resize-none"
                />
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => {
                      if (!headerForm.name.trim()) {
                        toast.error("Le nom est requis");
                        return;
                      }
                      updateMutation.mutate({
                        name: headerForm.name.trim(),
                        description: headerForm.description.trim() || null,
                      });
                      setEditingHeader(false);
                    }}
                    disabled={updateMutation.isPending}
                  >
                    <Check className="mr-1 h-3 w-3" />
                    Enregistrer
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingHeader(false)}
                  >
                    <X className="mr-1 h-3 w-3" />
                    Annuler
                  </Button>
                </div>
              </div>
            ) : (
              <div
                className="group cursor-pointer"
                onClick={() => {
                  setHeaderForm({
                    name: table.name,
                    description: table.description || "",
                  });
                  setEditingHeader(true);
                }}
              >
                <div className="flex items-center gap-2">
                  <h2 className="text-2xl font-bold">{table.name}</h2>
                  <code className="px-2 py-1 bg-muted rounded text-xs">
                    {table.slug}
                  </code>
                  <Pencil className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                {table.description ? (
                  <p className="text-muted-foreground mt-1">{table.description}</p>
                ) : (
                  <p className="text-muted-foreground/50 mt-1 text-sm opacity-0 group-hover:opacity-100 transition-opacity">
                    Cliquez pour ajouter une description
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {updateMutation.isPending && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Enregistrement...
              </div>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                setAutoFillStartRef("");
                setAutoFillDialogOpen(true);
              }}
              disabled={columns.length === 0 || data.length === 0}
            >
              <Wand2 className="mr-2 h-4 w-4" />
              Remplir réf. Excel
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport}>
              <Download className="mr-2 h-4 w-4" />
              Exporter
            </Button>
          </div>
        </div>

        {/* Columns section */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-info/10 flex items-center justify-center">
                <Columns3 className="h-5 w-5 text-info" />
              </div>
              <div>
                <CardTitle className="text-lg">Colonnes</CardTitle>
                <CardDescription>
                  {columns.length} colonne{columns.length > 1 ? "s" : ""} définies
                </CardDescription>
              </div>
            </div>
            <Button onClick={handleAddColumn} size="sm">
              <Plus className="mr-2 h-4 w-4" />
              Ajouter
            </Button>
          </CardHeader>
          <CardContent>
            {columns.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Columns3 className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p>Aucune colonne définie</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={handleAddColumn}>
                  Ajouter une colonne
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {columns.map((col) => (
                  <motion.div
                    key={col.name}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg border group hover:border-primary/50 transition-colors"
                  >
                    <span className="font-medium">{col.name}</span>
                    <Badge variant="outline" className="text-xs">
                      {col.type}
                    </Badge>
                    {col.required && (
                      <Badge variant="secondary" className="text-xs">
                        requis
                      </Badge>
                    )}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => handleEditColumn(col)}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-destructive"
                        onClick={() => setDeleteColumnDialog(col.name)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Data section */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-success/10 flex items-center justify-center">
                <Rows3 className="h-5 w-5 text-success" />
              </div>
              <div>
                <CardTitle className="text-lg">Données</CardTitle>
                <CardDescription>
                  {data.length} ligne{data.length > 1 ? "s" : ""}
                </CardDescription>
              </div>
            </div>
            <Button onClick={handleAddRow} size="sm" disabled={columns.length === 0}>
              <Plus className="mr-2 h-4 w-4" />
              Ajouter une ligne
            </Button>
          </CardHeader>
          <CardContent className="p-0">
            {columns.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground px-6">
                <AlertCircle className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p>Définissez d&apos;abord des colonnes avant d&apos;ajouter des données</p>
              </div>
            ) : data.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Rows3 className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p>Aucune donnée</p>
                <Button variant="outline" size="sm" className="mt-3" onClick={handleAddRow}>
                  Ajouter une ligne
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">#</TableHead>
                      {columns.map((col) => (
                        <TableHead key={col.name}>{col.name}</TableHead>
                      ))}
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.map((row, index) => {
                      const rowCellRefs = (row[CELL_REFS_FIELD] as CellRefs) || {};
                      return (
                      <TableRow key={index}>
                        <TableCell className="text-muted-foreground">{index + 1}</TableCell>
                        {columns.map((col) => (
                          <TableCell key={col.name}>
                            {editingRow?.index === index ? (
                              <CellEditor
                                value={editingRow.data[col.name]}
                                type={col.type}
                                onChange={(value) =>
                                  setEditingRow({
                                    ...editingRow,
                                    data: { ...editingRow.data, [col.name]: value },
                                  })
                                }
                                excelRef={editingRow.cellRefs[col.name]}
                                onExcelRefChange={(ref) =>
                                  setEditingRow({
                                    ...editingRow,
                                    cellRefs: { ...editingRow.cellRefs, [col.name]: ref },
                                  })
                                }
                                showExcelRefInput
                              />
                            ) : (
                              <CellDisplay 
                                value={row[col.name]} 
                                type={col.type} 
                                excelRef={rowCellRefs[col.name]}
                              />
                            )}
                          </TableCell>
                        ))}
                        <TableCell>
                          {editingRow?.index === index ? (
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-success"
                                onClick={handleSaveRow}
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={handleCancelEditRow}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleEditRow(index)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() => setDeleteRowIndex(index)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Column Dialog */}
      <Dialog open={columnDialogOpen} onOpenChange={setColumnDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingColumn ? "Modifier la colonne" : "Nouvelle colonne"}
            </DialogTitle>
            <DialogDescription>
              Définissez les propriétés de la colonne
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="col-name">Nom</Label>
              <Input
                id="col-name"
                value={columnForm.name}
                onChange={(e) => setColumnForm({ ...columnForm, name: e.target.value })}
                placeholder="nom_colonne"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="col-type">Type</Label>
              <Select
                value={columnForm.type}
                onValueChange={(value: ColumnType) =>
                  setColumnForm({ ...columnForm, type: value })
                }
              >
                <SelectTrigger id="col-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="string">Texte (string)</SelectItem>
                  <SelectItem value="number">Nombre (number)</SelectItem>
                  <SelectItem value="boolean">Booléen (boolean)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="col-required">Requis</Label>
              <Switch
                id="col-required"
                checked={columnForm.required}
                onCheckedChange={(checked) =>
                  setColumnForm({ ...columnForm, required: checked })
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setColumnDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveColumn}>
              {editingColumn ? "Modifier" : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Column Dialog */}
      <AlertDialog
        open={deleteColumnDialog !== null}
        onOpenChange={() => setDeleteColumnDialog(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la colonne</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer la colonne &quot;{deleteColumnDialog}&quot; ?
              Toutes les données de cette colonne seront perdues.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteColumnDialog && handleDeleteColumn(deleteColumnDialog)}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Row Dialog */}
      <Dialog open={addRowDialogOpen} onOpenChange={setAddRowDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouvelle ligne</DialogTitle>
            <DialogDescription>
              Remplissez les valeurs pour la nouvelle ligne
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            {columns.map((col) => (
              <div key={col.name} className="space-y-2">
                <Label htmlFor={`new-row-${col.name}`}>
                  {col.name}
                  {col.required && <span className="text-destructive ml-1">*</span>}
                  <Badge variant="outline" className="ml-2 text-xs">
                    {col.type}
                  </Badge>
                </Label>
                <CellEditor
                  value={newRowData[col.name]}
                  type={col.type}
                  onChange={(value) =>
                    setNewRowData({ ...newRowData, [col.name]: value })
                  }
                  id={`new-row-${col.name}`}
                  excelRef={newRowCellRefs[col.name]}
                  onExcelRefChange={(ref) =>
                    setNewRowCellRefs({ ...newRowCellRefs, [col.name]: ref })
                  }
                  showExcelRefInput
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddRowDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveNewRow}>
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Row Dialog */}
      <AlertDialog
        open={deleteRowIndex !== null}
        onOpenChange={() => setDeleteRowIndex(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la ligne</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer cette ligne ?
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteRowIndex !== null && handleDeleteRow(deleteRowIndex)}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Auto-fill Excel References Dialog */}
      <Dialog open={autoFillDialogOpen} onOpenChange={setAutoFillDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wand2 className="h-5 w-5" />
              Remplir les références Excel
            </DialogTitle>
            <DialogDescription>
              Entrez la référence de la première cellule (ligne 1, colonne 1).
              Les références seront générées automatiquement pour tout le tableau.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="auto-fill-start-ref">Référence de départ</Label>
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <Input
                  id="auto-fill-start-ref"
                  value={autoFillStartRef}
                  onChange={(e) => setAutoFillStartRef(e.target.value)}
                  placeholder="Ex: 'Costing Transport 2025'!H8"
                  className="font-mono"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Format: &apos;Nom de feuille&apos;!ColonneLigne (ex: &apos;Costing 2025&apos;!H8)
              </p>
            </div>

            {/* Preview */}
            {autoFillPreview && (
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">Aperçu du remplissage</Label>
                <div className="bg-muted/50 rounded-lg p-3 space-y-2 max-h-[200px] overflow-auto">
                  {autoFillPreview.preview.map(({ rowIdx, refs }) => (
                    <div key={rowIdx} className="flex items-start gap-2 text-xs">
                      <span className="text-muted-foreground w-12 flex-shrink-0">
                        Ligne {rowIdx + 1}:
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(refs).map(([colName, ref]) => (
                          <span key={colName} className="font-mono bg-background px-1.5 py-0.5 rounded border">
                            {ref}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                  {data.length > 3 && (
                    <p className="text-xs text-muted-foreground italic">
                      ... et {data.length - 3} autres lignes
                    </p>
                  )}
                </div>
              </div>
            )}

            {autoFillStartRef.trim() && !autoFillPreview && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                Format de référence invalide
              </div>
            )}

            {/* Summary */}
            <div className="bg-info/10 border border-info/20 rounded-lg p-3">
              <p className="text-sm">
                <strong>{data.length}</strong> ligne{data.length > 1 ? "s" : ""} × <strong>{columns.length}</strong> colonne{columns.length > 1 ? "s" : ""} = <strong>{data.length * columns.length}</strong> références à générer
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoFillDialogOpen(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleAutoFillExcelRefs}
              disabled={!autoFillPreview}
            >
              <Wand2 className="mr-2 h-4 w-4" />
              Appliquer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </AdminGuard>
  );
}

// Helper function to get default value based on type
function getDefaultValue(type: ColumnType): unknown {
  switch (type) {
    case "string":
      return "";
    case "number":
      return 0;
    case "boolean":
      return false;
    default:
      return "";
  }
}

// Cell display component
function CellDisplay({ value, type, excelRef }: { value: unknown; type: ColumnType; excelRef?: string }) {
  const renderContent = () => {
    if (value === null || value === undefined) {
      return <span className="text-muted-foreground italic">vide</span>;
    }
    
    if (type === "boolean") {
      return (
        <Badge variant={value ? "default" : "secondary"}>
          {value ? "Oui" : "Non"}
        </Badge>
      );
    }
    
    // For number type, check if it's a formula string
    if (type === "number" && typeof value === "string" && isNumericExpression(value)) {
      return (
        <span className="font-mono text-info dark:text-info" title="Formule (évaluée au calcul)">
          {value}
        </span>
      );
    }
    
    return <span>{String(value)}</span>;
  };

  // If there's an Excel reference, wrap in tooltip
  if (excelRef) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex items-center gap-1.5 cursor-help">
            {renderContent()}
            <FileSpreadsheet className="h-3 w-3 text-success dark:text-success flex-shrink-0" />
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 text-success" />
            <div>
              <p className="text-xs text-muted-foreground">Source Excel</p>
              <p className="font-mono text-sm">{excelRef}</p>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  return renderContent();
}

// Check if a string looks like a numeric expression (e.g. "0.2/3", "1+2*3")
function isNumericExpression(value: string): boolean {
  // Must contain at least one operator
  if (!/[+\-*/]/.test(value)) return false;
  // Should only contain digits, operators, dots, commas, spaces, and parentheses
  if (!/^[\d\s.,+\-*/()]+$/.test(value)) return false;
  // Must have at least one digit
  if (!/\d/.test(value)) return false;
  return true;
}

// Excel reference utilities
interface ParsedExcelRef {
  sheet: string;
  col: string;
  row: number;
}

/**
 * Parse an Excel reference like "'Costing Transport 2025'!H8" or "Sheet1!A1"
 * Returns null if the format is invalid
 */
function parseExcelRef(ref: string): ParsedExcelRef | null {
  // Match patterns like: 'Sheet Name'!A1 or SheetName!A1
  const match = ref.match(/^'([^']+)'!([A-Z]+)(\d+)$|^([^'!]+)!([A-Z]+)(\d+)$/i);
  if (!match) return null;
  
  // Extract sheet name, column, and row from either pattern
  const sheet = match[1] || match[4];
  const col = (match[2] || match[5]).toUpperCase();
  const row = parseInt(match[3] || match[6], 10);
  
  if (!sheet || !col || isNaN(row)) return null;
  
  return { sheet, col, row };
}

/**
 * Increment an Excel column letter: A→B, Z→AA, AA→AB, AZ→BA, etc.
 */
function incrementExcelCol(col: string): string {
  const chars = col.toUpperCase().split('');
  let i = chars.length - 1;
  
  while (i >= 0) {
    if (chars[i] === 'Z') {
      chars[i] = 'A';
      i--;
    } else {
      chars[i] = String.fromCharCode(chars[i].charCodeAt(0) + 1);
      return chars.join('');
    }
  }
  
  // All chars were Z, prepend A (e.g., ZZ → AAA)
  return 'A' + chars.join('');
}

/**
 * Build an Excel reference string from components
 */
function buildExcelRef(sheet: string, col: string, row: number): string {
  // Always quote sheet names with spaces or special characters
  const needsQuotes = /[^a-zA-Z0-9]/.test(sheet);
  const sheetPart = needsQuotes ? `'${sheet}'` : sheet;
  return `${sheetPart}!${col}${row}`;
}

/**
 * Generate a 2D array of Excel references for a grid
 * Starting from startRef, incrementing rows going down and columns going right
 */
function generateExcelRefsGrid(
  startRef: string,
  numRows: number,
  columnNames: string[]
): Map<number, CellRefs> {
  const parsed = parseExcelRef(startRef);
  if (!parsed) return new Map();
  
  const result = new Map<number, CellRefs>();
  
  for (let rowIdx = 0; rowIdx < numRows; rowIdx++) {
    const cellRefs: CellRefs = {};
    let currentCol = parsed.col;
    
    for (let colIdx = 0; colIdx < columnNames.length; colIdx++) {
      const colName = columnNames[colIdx];
      cellRefs[colName] = buildExcelRef(parsed.sheet, currentCol, parsed.row + rowIdx);
      currentCol = incrementExcelCol(currentCol);
    }
    
    result.set(rowIdx, cellRefs);
  }
  
  return result;
}

// Cell editor component
function CellEditor({
  value,
  type,
  onChange,
  id,
  excelRef,
  onExcelRefChange,
  showExcelRefInput = false,
}: {
  value: unknown;
  type: ColumnType;
  onChange: (value: unknown) => void;
  id?: string;
  excelRef?: string;
  onExcelRefChange?: (ref: string) => void;
  showExcelRefInput?: boolean;
}) {
  const renderValueInput = () => {
    if (type === "boolean") {
      return (
        <Switch
          id={id}
          checked={Boolean(value)}
          onCheckedChange={onChange}
        />
      );
    }
    
    if (type === "number") {
      // Use text input to allow formulas like "0.2/3" or "1+2*3"
      // The formula will be evaluated server-side during lookup
      return (
        <Input
          id={id}
          type="text"
          value={String(value ?? "")}
          onChange={(e) => {
            const val = e.target.value;
            // Normalize: replace comma with dot for decimal separator
            const normalized = val.replace(",", ".");
            
            // If empty, set to null (allows empty/null values)
            if (normalized === "") {
              onChange(null);
              return;
            }
            
            // If it looks like an expression (contains operators), store as string
            if (isNumericExpression(normalized)) {
              onChange(normalized);
              return;
            }
            
            // Keep the string value during editing to allow typing decimals
            // (e.g., "2234." should not become "2234" immediately)
            // Only convert to number if it's a complete valid number
            const endsWithDecimalOrIncomplete = /[.,]$/.test(normalized) || /[.,]\d*$/.test(normalized) && normalized.endsWith("0");
            
            if (endsWithDecimalOrIncomplete) {
              // Keep as string to preserve trailing dot or zeros
              onChange(normalized);
            } else {
              // Try to parse as number
              const num = parseFloat(normalized);
              onChange(isNaN(num) ? normalized : num);
            }
          }}
          onBlur={() => {
            // On blur, finalize the value as a number if possible
            // Keep null/empty values as null
            if (value === null || value === undefined || value === "") {
              onChange(null);
              return;
            }
            const val = String(value).replace(",", ".");
            if (val && !isNumericExpression(val)) {
              const num = parseFloat(val);
              if (!isNaN(num)) {
                onChange(num);
              }
            }
          }}
          placeholder="Valeur"
          className="h-8"
        />
      );
    }
    
    return (
      <Input
        id={id}
        type="text"
        value={String(value ?? "")}
        onChange={(e) => onChange(e.target.value)}
        className="h-8"
      />
    );
  };

  if (showExcelRefInput && onExcelRefChange) {
    return (
      <div className="space-y-2">
        {renderValueInput()}
        <div className="flex items-center gap-2">
          <FileSpreadsheet className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          <Input
            type="text"
            value={excelRef || ""}
            onChange={(e) => onExcelRefChange(e.target.value)}
            placeholder="Réf. Excel (ex: 'Costing 2025'!H7)"
            className="h-7 text-xs font-mono"
          />
        </div>
      </div>
    );
  }

  return renderValueInput();
}

